import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plugs, CheckCircle, XCircle, User, SlidersHorizontal } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LOCATIONS, SENIORITIES, DEPARTMENTS } from "@/lib/constants";

const Section = ({ icon: Icon, title, desc, children }) => (
  <div className="bg-white border border-zinc-200 rounded-xl p-6 mb-5">
    <div className="flex items-center gap-2 mb-1">
      <Icon size={18} className="text-zinc-900" />
      <h2 className="font-head text-lg font-semibold tracking-tight">{title}</h2>
    </div>
    {desc && <p className="text-sm text-zinc-500 mb-5">{desc}</p>}
    {children}
  </div>
);

const ProviderRow = ({ name, connected, value, setValue, onSave, onTest, testing }) => (
  <div className="border border-zinc-200 rounded-lg p-4 mb-3">
    <div className="flex items-center justify-between mb-3">
      <span className="font-semibold text-sm">{name}</span>
      <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${connected ? "text-emerald-600" : "text-zinc-400"}`}>
        {connected ? <CheckCircle size={14} weight="fill" /> : <XCircle size={14} />}
        {connected ? "Connected" : "Not Connected"}
      </span>
    </div>
    <div className="flex gap-2">
      <input data-testid={`${name.toLowerCase()}-key-input`} type="password" value={value} onChange={(e) => setValue(e.target.value)}
        placeholder={`${name} API Key`} className="flex-1 h-10 px-3 rounded-md border border-zinc-200 text-sm outline-none focus:border-zinc-900" />
      <button data-testid={`${name.toLowerCase()}-connect`} onClick={onSave} className="h-10 px-4 rounded-md bg-zinc-900 text-white text-sm hover:bg-zinc-800 transition-colors">Connect</button>
      <button data-testid={`${name.toLowerCase()}-test`} onClick={onTest} disabled={testing} className="h-10 px-4 rounded-md border border-zinc-200 text-sm hover:bg-zinc-50 transition-colors disabled:opacity-50">
        {testing ? "Testing…" : "Test"}
      </button>
    </div>
  </div>
);

export default function Settings() {
  const { user, checkAuth } = useAuth();
  const [hunter, setHunter] = useState("");
  const [apollo, setApollo] = useState("");
  const [status, setStatus] = useState({ hunter_connected: false, apollo_connected: false });
  const [testing, setTesting] = useState(null);

  const [loc, setLoc] = useState("Any");
  const [sen, setSen] = useState([]);
  const [depts, setDepts] = useState([]);
  const [title, setTitle] = useState("");

  useEffect(() => {
    api.get("/settings/providers").then((r) => setStatus(r.data));
    api.get("/settings/preferences").then((r) => {
      const p = r.data;
      setLoc(p.default_location || "Any");
      setSen(p.default_seniority || []);
      setDepts(p.default_departments || []);
    });
    setTitle(user?.professional_title || "");
  }, [user]);

  const saveProvider = async (key) => {
    await api.post("/settings/providers", key === "hunter" ? { hunter_api_key: hunter } : { apollo_api_key: apollo });
    const r = await api.get("/settings/providers");
    setStatus(r.data);
    toast.success("Saved");
  };
  const testProvider = async (key) => {
    setTesting(key);
    try {
      const r = await api.post(`/settings/providers/test/${key}`);
      r.data.connected ? toast.success(`${key}: ${r.data.message}`) : toast.error(`${key}: ${r.data.message}`);
    } finally { setTesting(null); }
  };

  const savePrefs = async () => {
    await api.post("/settings/preferences", {
      default_location: loc, default_seniority: sen, default_departments: depts, professional_title: title,
    });
    checkAuth();
    toast.success("Preferences saved");
  };

  const toggle = (arr, set, v) => set(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

  return (
    <div className="max-w-3xl mx-auto px-8 py-14">
      <h1 className="font-head text-3xl font-bold tracking-tight mb-8">Settings</h1>

      <Section icon={Plugs} title="Data Providers" desc="Connect Hunter or Apollo for live data. Keys are stored securely server-side and never exposed.">
        <ProviderRow name="Hunter" connected={status.hunter_connected} value={hunter} setValue={setHunter}
          onSave={() => saveProvider("hunter")} onTest={() => testProvider("hunter")} testing={testing === "hunter"} />
        <ProviderRow name="Apollo" connected={status.apollo_connected} value={apollo} setValue={setApollo}
          onSave={() => saveProvider("apollo")} onTest={() => testProvider("apollo")} testing={testing === "apollo"} />
      </Section>

      <Section icon={SlidersHorizontal} title="Search Preferences" desc="Defaults applied to new searches.">
        <div className="space-y-5">
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.15em] text-zinc-500 block mb-2">Default Location</label>
            <select data-testid="pref-location" value={loc} onChange={(e) => setLoc(e.target.value)} className="w-full h-10 px-3 rounded-md border border-zinc-200 text-sm outline-none focus:border-zinc-900">
              {LOCATIONS.map((l) => <option key={l}>{l}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.15em] text-zinc-500 block mb-2">Default Seniority</label>
            <div className="flex flex-wrap gap-2">
              {SENIORITIES.map((x) => (
                <button key={x} onClick={() => toggle(sen, setSen, x)} className={`px-3 h-8 rounded-md text-xs font-medium border transition-colors ${sen.includes(x) ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-600 border-zinc-200"}`}>{x}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.15em] text-zinc-500 block mb-2">Default Departments</label>
            <div className="flex flex-wrap gap-2">
              {DEPARTMENTS.map((x) => (
                <button key={x} onClick={() => toggle(depts, setDepts, x)} className={`px-3 h-8 rounded-md text-xs font-medium border transition-colors ${depts.includes(x) ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-600 border-zinc-200"}`}>{x}</button>
              ))}
            </div>
          </div>
        </div>
      </Section>

      <Section icon={User} title="Account">
        <div className="space-y-4">
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.15em] text-zinc-500 block mb-2">Name</label>
            <input value={user?.name || ""} disabled className="w-full h-10 px-3 rounded-md border border-zinc-200 bg-zinc-50 text-sm text-zinc-500" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.15em] text-zinc-500 block mb-2">Email</label>
            <input value={user?.email || ""} disabled className="w-full h-10 px-3 rounded-md border border-zinc-200 bg-zinc-50 text-sm text-zinc-500" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.15em] text-zinc-500 block mb-2">Professional Title (optional)</label>
            <input data-testid="pref-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Performance Marketing Manager" className="w-full h-10 px-3 rounded-md border border-zinc-200 text-sm outline-none focus:border-zinc-900" />
          </div>
        </div>
      </Section>

      <button data-testid="save-preferences" onClick={savePrefs} className="h-11 px-6 rounded-lg bg-zinc-900 text-white text-sm hover:bg-zinc-800 transition-colors">
        Save Changes
      </button>
    </div>
  );
}
